import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import RefreshIcon from "@redotech/redo-icons/refresh-cw-01.svg";
import RefreshFilledIcon from "@redotech/redo-icons/refresh-cw-01_filled.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  ReloadReplenishmentRequestViewsContext,
  ReplenishmentRequestViewCountsContext,
  ReplenishmentRequestViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/wms/replenishment-requests/replenishment-request-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useContext, useMemo } from "react";
import { OrderManagementSuiteStatusesContext } from "../../../../redo-suite/order-management-cloud";
import { useIsChildRouteActive } from "./use-child-route-active";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "./view-to-tree-utils";

export const ReplenishmentRequestsItem = memo(
  function ReplenishmentRequestsItem() {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { wmsEnabled } = useContext(OrderManagementSuiteStatusesContext);
    const viewsContext = useContext(ReplenishmentRequestViewsContext);
    const viewCountsContext = useContext(ReplenishmentRequestViewCountsContext);
    const reloadViews = useContext(ReloadReplenishmentRequestViewsContext);

    const wmsBaseUrl = `${routes.shippingBaseUrl}/wms`;
    const replenishmentRequestsBaseUrl = `${wmsBaseUrl}/replenishment-requests`;

    const isAnyChildActive = useIsChildRouteActive(
      replenishmentRequestsBaseUrl,
      [],
    );

    const allViews = useMemo(() => {
      return [...(viewsContext?.views ?? [])];
    }, [viewsContext]);

    const allFolders = useMemo(() => {
      return [...(viewsContext?.folders ?? [])];
    }, [viewsContext]);

    const viewsWithoutFolders = useMemo(() => {
      return allViews.filter((view) => !view.folderId);
    }, [allViews]);

    const foldersWithViews = useMemo<
      FolderWithView<"replenishment-requests">[]
    >(() => {
      return allFolders.map((folder) => {
        return {
          folder,
          views: allViews.filter((view) => view.folderId === folder._id),
        };
      });
    }, [allFolders, allViews]);

    const sortableItems = useMemo(() => {
      return viewsAndFoldersToTree({
        viewsWithoutFolders,
        foldersWithViews,
        urlPrefix: replenishmentRequestsBaseUrl,
        viewCounts: viewCountsContext?.viewCounts,
        folderCounts: viewCountsContext?.folderCounts,
        hideZeroNotifications: true,
      });
    }, [
      viewsWithoutFolders,
      foldersWithViews,
      replenishmentRequestsBaseUrl,
      viewCountsContext?.viewCounts,
      viewCountsContext?.folderCounts,
    ]);

    const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
      viewsContext?.openEditFolderModal(id.toString());
    });

    const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
      const flattenedItems = flattenTree(items);
      const { views, folders } =
        treeToViewsAndFolders<"replenishment-requests">(
          flattenedItems,
          allViews,
          allFolders,
        );
      viewsContext?.updateOrdering(views, folders);
      void reloadViews?.();
    });

    if (!wmsEnabled) {
      return null;
    }

    return (
      <RedoSecondaryNavItem
        action={{ type: "button" }}
        anyChildSelected={isAnyChildActive}
        FilledIcon={RefreshFilledIcon}
        friendlyName="Replenishment requests"
        id="replenishment-requests"
        UnfilledIcon={RefreshIcon}
      >
        <SortableTree
          items={sortableItems}
          onRequestEdit={handleRequestEdit}
          setItems={handleUpdateOrdering}
        />
        <ReplenishmentRequestsAddViewItem
          href={`${replenishmentRequestsBaseUrl}/create`}
        />
        <ReplenishmentRequestsCreateFolderItem />
      </RedoSecondaryNavItem>
    );
  },
);

const ReplenishmentRequestsAddViewItem = memo(
  function ReplenishmentRequestsAddViewItem({ href }: { href: string }) {
    return (
      <RedoSecondaryNavItem
        action={{ type: "link", href }}
        friendlyName="+ Add new view"
        id="replenishment-requests-create-view"
      />
    );
  },
);

const ReplenishmentRequestsCreateFolderItem = memo(
  function ReplenishmentRequestsCreateFolderItem() {
    const viewsContext = useContext(ReplenishmentRequestViewsContext);

    return (
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            viewsContext?.openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="replenishment-requests-create-folder"
      />
    );
  },
);
